import { Gtin, GtinType } from './gtin.model';

const ARTICLE_NUMBER_LENGTH: number = 6;

const GTIN_TYPE_BY_LENGTH: Record<number, GtinType> = {
  // Note: the 6-digit "ARTICLE-6" type is included for compatibility with the existing system, even though it's not a standard GTIN format
  // Article numbers without a leading zero, so 5 digits are valide article numbers too.
  [ARTICLE_NUMBER_LENGTH - 1]: 'ARTICLE-6',
  [ARTICLE_NUMBER_LENGTH]: 'ARTICLE-6',
  8: 'GTIN-8',
  12: 'GTIN-12',
  13: 'GTIN-13',
  14: 'GTIN-14',
};

export class GtinHelper {
  public static normalize(value: string): string {
    return String(value).replace(/\D/g, '');
  }

  public static isNumeric(value: string): boolean {
    return /^\d+$/.test(value);
  }

  /**
   * Calculates the GS1 check digit for a GTIN body without its final check digit.
   *
   * The algorithm processes the digits from right to left, multiplies them alternately
   * by 3 and 1, sums the results, and returns the amount needed to reach the next
   * multiple of 10.
   *
   * Used for GTIN-8, GTIN-12, GTIN-13, and GTIN-14 validation in {@link validate}.
   *
   * @see https://www.gs1.org/services/how-calculate-check-digit-manually
   */
  public static calculateCheckDigit(gtinWithoutCheckDigit: string): number {
    let sum: number = 0;

    for (let i: number = 0; i < gtinWithoutCheckDigit.length; i++) {
      const digit: number = Number(gtinWithoutCheckDigit[gtinWithoutCheckDigit.length - 1 - i]);

      sum += digit * (i % 2 === 0 ? 3 : 1);
    }

    return (10 - (sum % 10)) % 10;
  }

  public static validate(value: string): Gtin | null {
    // Determine the GTIN type from the normalized value length.
    const type: GtinType | undefined = GTIN_TYPE_BY_LENGTH[value.length];

    // Unsupported lengths cannot be validated as a known GTIN or article number.
    if (!type) {
      return null;
    }

    // ARTICLE-6 is a legacy compatibility format and does not use a GS1 check digit.
    // 5-digit article numbers get their leading zero back here.
    if (type === 'ARTICLE-6') {
      return {
        raw: value,
        normalized: value.padStart(ARTICLE_NUMBER_LENGTH, '0'),
        type,
        isValid: true,
      };
    }

    // Calculate the expected check digit from the GTIN body and compare it to the provided one.
    const expectedCheckDigit: number = GtinHelper.calculateCheckDigit(value.slice(0, -1));
    const actualCheckDigit: number = Number(value.slice(-1));

    // Return the parsed GTIN metadata together with the validation result.
    return {
      raw: value,
      normalized: value,
      type,
      isValid: expectedCheckDigit === actualCheckDigit,
    };
  }
}
